@let loading = (loading$ | async) ?? false;

<div class="create-questions-container">

    <!-- Left Side: Form Content -->
    <div class="form-section mt-8 mb-4" [class.drawer-open]="chatOpen">

        <div class="flex justify-between items-center w-full mb-4">
            <div class="flex flex-row justify-between">
                <app-btn-back></app-btn-back>
            </div>
        </div>

        <div class="flex flex-col gap-2 mb-4 border-0 w-full">
            <h1 class="font-semibold text-2xl text-primary-500">
                Add Questions
            </h1>
        </div>

        @if (isStreaming) {
        <div
            class="flex items-center gap-2 p-3 mb-4 bg-indigo-50 border border-indigo-100 rounded-lg text-indigo-700 text-sm">
            <i class="fas fa-robot animate-bounce text-indigo-500"></i>
            <span class="font-medium">AI is generating and syncing questions...</span>
            <div class="flex gap-1 ml-auto items-center">
                <span class="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-bounce"
                    style="animation-delay: 0.1s"></span>
                <span class="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-bounce"
                    style="animation-delay: 0.2s"></span>
                <span class="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-bounce"
                    style="animation-delay: 0.3s"></span>
            </div>
        </div>
        }

        @if (isSearchingDb) {
        <div
            class="flex items-center gap-2 p-3 mb-4 bg-amber-50 border border-amber-100 rounded-lg text-amber-700 text-sm">
            <i class="fas fa-search animate-pulse text-amber-500"></i>
            <span class="font-medium">AI is searching the database for pre-existing questions...</span>
            <div class="flex gap-1 ml-auto items-center">
                <span class="w-1.5 h-1.5 bg-amber-600 rounded-full animate-bounce"
                    style="animation-delay: 0.1s"></span>
                <span class="w-1.5 h-1.5 bg-amber-600 rounded-full animate-bounce"
                    style="animation-delay: 0.2s"></span>
                <span class="w-1.5 h-1.5 bg-amber-600 rounded-full animate-bounce"
                    style="animation-delay: 0.3s"></span>
            </div>
        </div>
        }

        <div class="w-full mb-6">
            <form [formGroup]="mainForm" (ngSubmit)="onSubmit()" class="space-y-4">

                <div formArrayName="questions" class="space-y-3">

                    @for (question of questions.controls; track question; let qIdx = $index) {

                    <div [formGroupName]="qIdx" [id]="'question-' + qIdx"
                        class="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden transition-all">

                        <div (click)="toggleCollapse(qIdx)"
                            class="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 bg-gray-50/50">
                            <div class="flex-1 truncate mr-4">
                                <strong class="text-secondary-500 mr-2">#{{ qIdx + 1 }}</strong>

                                @if (!isExpanded(qIdx)) {
                                <span class="text-sm font-medium text-gray-600">
                                    @let qText = question.get('questionText')?.value;
                                    @let ref = question.get('reference')?.value;

                                    {{ qText || '(Empty Question)' }}

                                    @if (ref?.book) {
                                    <span class="ml-2 text-xs text-gray-400 italic">
                                        — {{ ref.book | titlecase }} {{ ref.chapter }}:{{ ref.verse }}
                                    </span>
                                    }
                                </span>
                                } @else {
                                <span class="text-xs font-bold uppercase text-gray-400 tracking-widest">
                                    Editing Question
                                </span>
                                }
                            </div>

                            <div class="flex items-center gap-4">
                                @if (question.invalid && question.touched) {
                                <div class="h-2 w-2 rounded-full bg-red-500"></div>
                                }

                                <svg xmlns="http://www.w3.org/2000/svg" [class.rotate-180]="isExpanded(qIdx)"
                                    class="h-5 w-5 text-gray-400 transition-transform duration-200" fill="none"
                                    viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M19 9l-7 7-7-7" />
                                </svg>

                                <button type="button" (click)="removeQuestion(qIdx); $event.stopPropagation()"
                                    class="text-gray-300 hover:text-red-500">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none"
                                        viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div [class.hidden]="!isExpanded(qIdx)" class="p-4 border-t border-gray-100 space-y-4 bg-white">

                            <div>
                                <label class="form-label">Reference *</label>
                                <bible-reference-picker formControlName="reference" [verseRequired]="false"
                                    [bibleEnum]="bibleBooks"></bible-reference-picker>
                                @if (question.get('reference')?.touched && question.get('reference')?.invalid) {
                                <div class="mt-1 flex items-center gap-1 text-xs text-red-600 tracking-wide">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20"
                                        fill="currentColor">
                                        <path fill-rule="evenodd"
                                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                            clip-rule="evenodd" />
                                    </svg>
                                    Reference incorrect
                                </div>
                                }
                            </div>

                            <div>
                                <label class="form-label">Question Text *</label>
                                <textarea formControlName="questionText" rows="2" required
                                    class="w-full p-2 border border-gray-200 rounded focus:ring-2 focus:ring-secondary-500 focus:outline-none"></textarea>
                            </div>

                            <div class="w-full flex flex-col gap-1 mb-4">
                                <label class="form-label">
                                    Explanation (Optional)
                                </label>
                                <textarea formControlName="explanation" rows="2"
                                    class="w-full p-2 border border-gray-200 rounded focus:ring-2 focus:ring-secondary-500 focus:outline-none"></textarea>
                            </div>

                            <div class="grid grid-cols-2 gap-4">
                                <div>
                                    <label class="form-label">Difficulty (1-10) *</label>
                                    <input formControlName="difficulty" type="number" min="1"
                                        class="w-full p-2 border border-gray-200 rounded focus:ring-2 focus:ring-secondary-500">
                                </div>
                                <div>
                                    <label class="form-label">Score Points *</label>
                                    <input formControlName="score" type="number" min="1"
                                        class="w-full p-2 border border-gray-200 rounded focus:ring-2 focus:ring-secondary-500">
                                </div>
                            </div>

                            <div>
                                <label class="form-label">Question Type *</label>
                                <select formControlName="questionType"
                                    class="w-full p-2 border border-gray-200 rounded focus:ring-2 focus:ring-secondary-500 text-primary-500">
                                    @for (qType of questionTypesList; track qType.key) {
                                    <option [value]="qType.key" class="bg-white text-primary-500 text-sm">
                                        {{ qType.key | questionTypeLabel }}
                                    </option>
                                    }
                                </select>
                            </div>

                            <!-- Search Tags Editor -->
                            <div class="space-y-2 mb-4">
                                <label class="form-label text-secondary-600 font-bold">Search Tags (Max 10)</label>
                                @let tags = question.get('tags')?.value || [];
                                <div
                                    class="flex flex-wrap gap-1.5 p-2.5 border border-gray-200 rounded bg-gray-50/50 min-h-[40px] items-center">
                                    @for (tag of tags; track tag; let tIdx = $index) {
                                    <span
                                        class="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 font-medium px-2.5 py-1 rounded-full border border-indigo-200">
                                        #{{ tag }}
                                        <button type="button" (click)="removeTag(qIdx, tIdx)"
                                            class="text-indigo-400 hover:text-red-500 transition-colors focus:outline-none ml-1">
                                            <i class="fas fa-times text-[10px]"></i>
                                        </button>
                                    </span>
                                    } @empty {
                                    <span class="text-xs text-gray-400 italic">No tags added yet. AI will suggest tags
                                        or you can add custom tags below.</span>
                                    }
                                </div>
                                <div class="flex gap-2">
                                    <input #tagInput type="text" placeholder="Type a tag and click Add or press Enter"
                                        (keyup.enter)="addTag(qIdx, $event); tagInput.value = ''"
                                        class="flex-grow p-2 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-secondary-500 outline-none">
                                    <button type="button" (click)="addTag(qIdx, { target: tagInput })"
                                        class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-sm font-semibold transition duration-200">
                                        Add
                                    </button>
                                </div>
                            </div>

                            @if (question.get('questionType')?.value === 'multipleChoice') {
                            <div class="space-y-3">
                                <div class="flex justify-between items-center">
                                    <label class="form-label !mb-0 text-secondary-600 font-bold">Answer Options
                                        *</label>
                                    <button type="button" (click)="addOption(qIdx)"
                                        class="text-xs bg-secondary-50 text-secondary-600 px-2 py-1 rounded border border-secondary-200 hover:bg-secondary-100">
                                        <i class="fas fa-plus"></i> Add Option
                                    </button>
                                </div>

                                @let optionsArray = getOptions(qIdx);

                                <div formArrayName="options" class="space-y-2">
                                    @for (opt of optionsArray.controls; track opt; let optIdx = $index) {

                                    @let optGroup = $any(opt);
                                    @let currentOptId = optGroup.get('id')?.value;
                                    @let isCorrect = question.get('correctOptionId')?.value === currentOptId;

                                    <div [formGroupName]="optIdx"
                                        class="flex items-center gap-3 p-2 rounded-md border transition-colors duration-200"
                                        [class.bg-green-50]="isCorrect" [class.border-green-200]="isCorrect"
                                        [class.bg-gray-50]="!isCorrect" [class.border-gray-100]="!isCorrect">

                                        <input type="checkbox" [name]="'correct-' + qIdx" [checked]="isCorrect"
                                            (change)="setCorrectOption(question, currentOptId)"
                                            class="h-4 w-4 rounded border-gray-300 text-green-500 bg-green-500 focus:bg-green-500 outline-none focus:outline-none focus:ring-0 focus:text-green-500">

                                        <input formControlName="text" placeholder="Option text..."
                                            class="flex-1 bg-transparent border-none focus:ring-0 text-sm py-1"
                                            [class.text-green-900]="isCorrect" [class.font-medium]="isCorrect">

                                        <button type="button" (click)="removeOption(qIdx, optIdx)"
                                            class="transition-colors outline-none focus:outline-none focus:ring-0"
                                            [class.text-green-400]="isCorrect" [class.hover:text-red-500]="true"
                                            [class.text-gray-400]="!isCorrect">
                                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none"
                                                viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                    }
                                </div>
                                @if (question.get('correctOptionId')?.invalid &&
                                question.get('correctOptionId')?.touched) {
                                <p class="text-xs text-red-500">Please select a correct answer.</p>
                                }
                            </div>
                            } @else {
                            <div class="space-y-1">
                                <label class="form-label text-secondary-600 font-bold">Correct Answer *</label>
                                @if (question.get('questionType')?.value === 'trueFalse') {
                                <select formControlName="simpleAnswer"
                                    class="w-full p-2 border border-gray-200 rounded focus:ring-2 focus:ring-secondary-500 text-primary-500">
                                    <option [value]="'True'" class="bg-white text-primary-500 text-sm">
                                        True</option>
                                    <option [value]="'False'" class="bg-white text-primary-500 text-sm">
                                        False</option>
                                </select>
                                } @else {
                                <input formControlName="simpleAnswer" placeholder="Enter the correct answer..."
                                    class="w-full p-2 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-secondary-500 outline-none">
                                }
                            </div>
                            }
                        </div>
                    </div>
                    } @empty {
                    <div class="p-8 text-center border-2 border-dashed border-gray-200 rounded-lg text-gray-400">
                        No questions added yet. Click "Add Another" or toggle the AI Assistant to start.
                    </div>
                    }
                </div>

                <div class="grid grid-rows-1 grid-cols-2 gap-4 w-full">
                    <app-btn-action type="button" (click)="addQuestion()" [isDisabled]="questions.length >= 10 || loading"
                        bgColor="border border-gray-300 bg-gray-100 hover:bg-primary-500 hover:border-primary-500 disabled:bg-gray-200"
                        [shadow]="false" textColor="text-primary-500 hover:text-white" [fullWidth]="true"
                        label="Add Another"></app-btn-action>

                    <app-btn-submit [isDisabled]="mainForm.invalid || loading || questions.length === 0"
                        [isLoading]="loading" [fullWidth]="true" actionText="Submit Questions"
                        class="flex-grow w-full"></app-btn-submit>
                </div>

            </form>
        </div>

    </div>

    <!-- Right Side: AI Chat Drawer -->
    <div class="chat-drawer shadow-2xl" [class.open]="chatOpen">
        <div class="chat-header">
            <div class="chat-title-area">
                <span class="chat-title"><i class="fas fa-magic mr-1.5"></i>AI Bible Quiz Companion</span>
                <span class="chat-subtitle">Create & refine questions interactively</span>
            </div>
            <button type="button" (click)="toggleChat()" class="close-btn">
                <i class="fas fa-times"></i>
            </button>
        </div>

        <div class="chat-history" id="chat-history-container">
            @for (msg of chatMessages; track msg; let idx = $index) {
            <div class="message-wrapper" [class.user]="msg.sender === 'user'" [class.ai]="msg.sender === 'ai'">
                <span class="message-sender">{{ msg.sender === 'user' ? 'You' : 'AI Companion' }}</span>
                <div class="message-bubble whitespace-pre-line">
                    {{ msg.text }}
                </div>
            </div>
            }

            @if (isSearchingDb) {
            <div class="message-wrapper ai">
                <span class="message-sender">AI Companion</span>
                <div class="message-bubble whitespace-pre-line flex flex-col gap-2 bg-amber-50/60 border border-amber-100/80 text-amber-800">
                    <div class="flex gap-1.5 items-center text-xs font-semibold">
                        <i class="fas fa-search animate-pulse text-amber-500"></i>
                        <span>Searching database for pre-existing questions...</span>
                        <span class="w-1.5 h-1.5 bg-amber-600 rounded-full animate-bounce"
                            style="animation-delay: 0.1s"></span>
                        <span class="w-1.5 h-1.5 bg-amber-600 rounded-full animate-bounce"
                            style="animation-delay: 0.2s"></span>
                        <span class="w-1.5 h-1.5 bg-amber-600 rounded-full animate-bounce"
                            style="animation-delay: 0.3s"></span>
                    </div>
                </div>
            </div>
            }

            @if (isStreaming && !isSearchingDb) {
            <div class="message-wrapper ai">
                <span class="message-sender">AI Companion</span>
                @if (streamingText) {
                <div class="message-bubble whitespace-pre-line flex flex-col gap-2">
                    <div>{{ streamingText }}</div>
                    <div
                        class="mt-2.5 pt-2 border-t border-gray-100 flex gap-1.5 items-center text-xs text-indigo-600 font-medium">
                        <i class="fas fa-sync animate-spin text-[10px]"></i>
                        <span>Syncing questions to form...</span>
                        <span class="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce"
                            style="animation-delay: 0.1s"></span>
                        <span class="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce"
                            style="animation-delay: 0.2s"></span>
                        <span class="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce"
                            style="animation-delay: 0.3s"></span>
                    </div>
                </div>
                } @else {
                <div class="message-bubble typing-bubble">
                    <div class="typing-dot"></div>
                    <div class="typing-dot"></div>
                    <div class="typing-dot"></div>
                </div>
                }
            </div>
            }
        </div>

        <div class="bg-white border-t border-gray-200/80 flex flex-col shrink-0">
            <div class="flex items-stretch min-h-[60px]">
                <textarea [(ngModel)]="chatInput" (keyup.enter)="sendChatMessage(); $event.preventDefault()"
                    placeholder="Create questions from Hebrews 11..." rows="2"
                    class="flex-1 bg-transparent border-0 resize-none py-2 px-4 text-sm text-primary-500 outline-none focus:ring-0 focus:outline-none placeholder-gray-400"></textarea>
                <button type="button" (click)="sendChatMessage()" [disabled]="!chatInput.trim() || isStreaming"
                    class="px-6 bg-gradient-to-br from-secondary-500 to-secondary-600 text-white flex items-center justify-center hover:from-secondary-600 hover:to-secondary-700 disabled:from-gray-200 disabled:to-gray-200 disabled:text-gray-400 transition-all duration-200 cursor-pointer disabled:cursor-not-allowed">
                    <i class="fas fa-paper-plane text-xl"></i>
                </button>
            </div>
            @if (totalChatTokens.totalTokens > 0) {
            <div class="text-[10px] text-gray-400 font-semibold tracking-wider uppercase pr-6 pb-2 self-end">
                Tokens: {{ totalChatTokens.totalTokens }}
            </div>
            }
        </div>

    </div>

    <!-- AI Floating Action Button -->
    <button type="button" (click)="toggleChat()" class="ai-fab" [class.ai-shimmer]="isStreaming">
        @if (chatOpen) {
        <i class="fas fa-times"></i>
        } @else {
        <i class="fas fa-robot animate-bounce" style="animation-duration: 2s;"></i>
        }
    </button>

</div>